import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { SiteMapUrl } from 'src/app/core/_enums/sitemap.url';
import { SnackbarTypes } from 'src/app/core/_enums/snackbar.types';
import { Indicator } from 'src/app/core/_models/indicator';
import { Member } from 'src/app/core/_models/member';
import { Operation } from 'src/app/core/_models/operation';
import { Project } from 'src/app/core/_models/project';
import { ProjectState } from 'src/app/core/_models/projectState';
import { ProjectType } from 'src/app/core/_models/projectType';
import { PublishProject } from 'src/app/core/_models/publishProject';
import { Scenario } from 'src/app/core/_models/scenario';
import { Solution } from 'src/app/core/_models/solution';
import { Version } from 'src/app/core/_models/version';
import { AgencyService } from 'src/app/core/_services/agency.service';
import { CallForTenderService } from 'src/app/core/_services/call-for-tender.service';
import { ProjectAutoSaveService } from 'src/app/core/_services/project-auto-save.service';
import { ProjectService } from 'src/app/core/_services/project.service';
import { SnackbarService } from 'src/app/core/_services/snackbar.service';
import { SpinnerOverlayService } from 'src/app/core/_services/spinner.overlay.service';
import { UploadService } from 'src/app/core/_services/upload.service';
import { UpdateResourceAction } from 'src/app/store/actions/resource.actions';
import { AppState } from 'src/app/store/app.state';
import { BreadcrumbItem } from '../shared/breadcrumbs/breadcrumbs.component';
import { ConfirmCancelDialogComponent } from '../shared/confirm-cancel-dialog/confirm-cancel-dialog.component';
import { DialogLeaveComponent } from '../shared/dialog-leave/dialog-leave.component';
import { DialogOverviewComponent } from '../shared/dialog-overview/dialog-overview.component';
import { DialogSelectAgencyComponent } from '../shared/dialog-select-agency/dialog-select-agency.component';
import { DialogSelectEuPublishComponent } from '../shared/dialog-select-eu-publish/dialog-select-eu-publish.component';
import { DialogShareProjectComponent } from '../shared/dialog-share-project/dialog-share-project.component';
import { TranslateService } from '@ngx-translate/core';
import { ProjectDataService } from './project-data.service';
import { assignCreatedEquipmentIds, PendingEquipmentProof } from './project-equipment-proofs.util';
import { computeProjectErrors } from './project-errors.util';
import { PROJECT_STATUSES, PROJECT_STEPS, STEP_LABELS } from './project.constants';

@Component({
  selector: 'app-project',
  templateUrl: './project.component.html',
  styleUrls: ['./project.component.scss']
})
export class ProjectComponent implements OnInit, OnDestroy {
  PROJECT_STEPS = PROJECT_STEPS;
  steps = STEP_LABELS;
  currentStep: number = this.PROJECT_STEPS.PROJECT;

  projectForm!: FormGroup;
  currentProject!: Project;

  projectTypes: ProjectType[] = [];
  databases: Version[] = [];
  contractors: Member[] = [];
  indicators: Indicator[] = [];
  filteredContractors!: Observable<{ id: string; displayName: string }[]>;
  projectStatuses = PROJECT_STATUSES;
  projectId: string | null = null;
  cftId: string | null = null;
  canShare: boolean = false;
  canExport: boolean = false;
  autoSaveEnabled: boolean = true;
  autoSaveBusy = false;
  flushChanges = (): Promise<boolean> => this.clickSave();
  private destroy$ = new Subject<void>();
  private pendingEquipmentProofs: PendingEquipmentProof[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private spinnerService: SpinnerOverlayService,
    private activeRoute: ActivatedRoute,
    private projectService: ProjectService,
    private callForTenderService: CallForTenderService,
    private snackbarService: SnackbarService,
    private router: Router,
    private store: Store<AppState>,
    private dialog: MatDialog,
    private agencyService: AgencyService,
    private projectDataService: ProjectDataService,
    private translateService: TranslateService,
    private autoSaveService: ProjectAutoSaveService,
    private uploadService: UploadService
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.setupUnsavedChangesWatcher();
    this.loadInitialData();
    this.setupRouteWatcher();
    this.setupContractorFilter();
  }

  ngOnDestroy(): void {
    this.autoSaveService.markSaved();
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (!this.hasUnsavedChanges) return;

    event.preventDefault();
    event.returnValue = true;
  }

  get hasUnsavedChanges(): boolean {
    return this.autoSaveService.hasUnsavedChanges;
  }

  async canDeactivate(): Promise<boolean> {
    if (!this.hasUnsavedChanges) return true;

    const confirmed = await this.dialog.open(DialogLeaveComponent).afterClosed().toPromise();
    return !!confirmed;
  }

  async onAutoSaveToggle(): Promise<void> {
    if (this.autoSaveBusy) return;
    if (this.autoSaveEnabled) {
      this.autoSaveEnabled = false;
      this.autoSaveService.setEnabled(this.projectId, false);
      return;
    }

    this.autoSaveBusy = true;
    try {
      const saved = !this.hasUnsavedChanges || (await this.clickSave());
      if (!saved) return;

      this.autoSaveEnabled = true;
      this.autoSaveService.setEnabled(this.projectId, true);
    } finally {
      this.autoSaveBusy = false;
    }
  }

  private setupUnsavedChangesWatcher(): void {
    this.projectForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.autoSaveEnabled || !this.projectForm.dirty) return;

      this.autoSaveService.markUnsaved();
    });
  }

  private initializeForm(): void {
    this.currentStep = history.state?.step || this.PROJECT_STEPS.PROJECT;
    this.projectForm = this.formBuilder.group({
      ...this.getBasicFields(),
      ...this.getOptionalFields(),
      ...this.getMetadataFields()
    });
  }

  private getBasicFields() {
    return {
      id: [null],
      code: [null, [Validators.required, Validators.maxLength(10)]],
      title1: [null, Validators.required],
      displayName: [null],
      projectType: [null, Validators.required],
      versionId: [null, Validators.required],
      projectContractorObject: [null, Validators.required]
    };
  }

  private getOptionalFields() {
    return {
      customerRef: [null],
      moaComplement: [null],
      useWaterAndBiodeversityIndicators: [false],
      state: [null],
      offerSubmissionDate: [null],
      reportTransmissionDate: [null]
    };
  }

  private getMetadataFields() {
    return {
      updateDate: [null],
      creator: [null],
      modifier: [null],
      createDate: [null],
      owner: [null]
    };
  }

  private loadInitialData(): void {
    this.projectDataService.loadProjectData().subscribe(({ moList, versions, projectTypes }) => {
      this.contractors = moList;
      this.projectTypes = projectTypes;
      this.databases = versions;
      this.formCrls.versionId.setValue(this.getLatestVersion()?.versionId);
      this.prefillMoaIfNewProject();
    });

    this.projectService.getIndicators().subscribe((indicators: Indicator[]) => {
      this.indicators = indicators;
    });
  }

  private prefillMoaIfNewProject(): void {
    if (this.projectId) return;
    if (!this.contractors?.length) return;
    if (this.formCrls.projectContractorObject.value) return;

    const userMo = this.contractors.find((c) => c.memberOf === true);
    if (!userMo) return;

    this.formCrls.projectContractorObject.setValue({
      id: userMo.memberId || '',
      displayName: userMo.displayName || ''
    });
  }

  private setupRouteWatcher(): void {
    this.activeRoute.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const id = params['id'];
      const fromCft = this.activeRoute.snapshot.queryParams['fromCft'] || null;
      this.loadProject(id, fromCft);
    });
  }

  private loadProject(id: string, fromCft: string | null = null): void {
    const navigationStep = history.state?.step;
    this.currentStep = navigationStep !== undefined ? navigationStep : this.PROJECT_STEPS.PROJECT;

    this.autoSaveService.markSaved();
    this.pendingEquipmentProofs = [];

    if (id && id !== 'new') {
      this.spinnerService.show();
      this.projectId = id;
      this.cftId = null;
      this.autoSaveEnabled = this.autoSaveService.isEnabled(id);
      this.projectService.get(id).subscribe(
        (item) => {
          this.canExport = item.canExport || false;

          this.currentProject = item;
          this.refreshProjectErrors();
          this.projectForm.reset();
          this.projectForm.patchValue({
            id: item.id,
            ...item,
            versionId: item.versionId || this.getLatestVersion()?.versionId,
            projectContractorObject: item.projectContractorObject || null
          });
          this.spinnerService.hide();
        },
        (error) => {
          this.snackbarService.open(SnackbarTypes.error);
          this.spinnerService.hide();
          this.router.navigate([SiteMapUrl.MenuProject]);
        }
      );
      return;
    }

    this.projectId = null;
    this.autoSaveEnabled = true;

    if (fromCft) {
      this.spinnerService.show();
      this.cftId = fromCft;
      this.projectService.getFromCft(fromCft).subscribe({
        next: (item) => {
          this.currentProject = item;
          this.projectForm.reset();
          this.projectForm.patchValue({
            ...item,
            versionId: item.versionId || this.getLatestVersion()?.versionId,
            projectContractorObject: item.projectContractorObject || null
          });
          this.spinnerService.hide();
        },
        error: () => {
          this.cftId = null;
          this.snackbarService.open(SnackbarTypes.error);
          this.initEmptyProjectForm();
          this.spinnerService.hide();
        }
      });
      return;
    }

    this.cftId = null;
    this.initEmptyProjectForm();
    this.spinnerService.hide();
  }

  private initEmptyProjectForm(): void {
    this.currentProject = {} as Project;
    this.projectForm.reset();
    this.formCrls.versionId.setValue(this.getLatestVersion()?.versionId);
    this.prefillMoaIfNewProject();
  }

  private refreshProjectErrors(): void {
    computeProjectErrors(this.currentProject);
  }

  get projectHasError(): boolean {
    return this.currentProject?.solutions?.some((s) => !!s.hasError) ?? false;
  }

  private getLatestVersion(): Version | null {
    if (!this.databases || this.databases.length === 0) {
      return null;
    }

    return this.databases.reduce((latest, current) => {
      if (!latest.versionDate) return current;
      if (!current.versionDate) return latest;

      return new Date(current.versionDate) > new Date(latest.versionDate) ? current : latest;
    });
  }

  private setupContractorFilter(): void {
    this.filteredContractors = this.formCrls.projectContractorObject.valueChanges.pipe(
      startWith(''),
      map((value) => this._filterContractors(value || '')),
      takeUntil(this.destroy$)
    );

    this.formCrls.projectContractorObject.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.handleContractorSelection(value);
    });
  }

  private handleContractorSelection(value: any): void {
    if (typeof value === 'object' && value?.id) {
      this.formCrls.projectContractorObject.setValue(value, { emitEvent: false });
    }
  }

  private _filterContractors(value: string | { id: null; displayName: string }): { id: string; displayName: string }[] {
    const filterValue = typeof value === 'string' ? value.toLowerCase() : value.displayName?.toLowerCase() || '';
    return this.contractors
      .filter((contractor) => contractor.displayName?.toLowerCase().includes(filterValue))
      .map((contractor) => ({
        id: contractor.memberId || '',
        displayName: contractor.displayName || ''
      }));
  }

  compareProjectTypes(pt1: ProjectType, pt2: ProjectType): boolean {
    return pt1 && pt2 ? pt1.projectTypeId === pt2.projectTypeId : pt1 === pt2;
  }

  get formCrls() {
    return this.projectForm.controls;
  }

  get breadcrumbItems(): BreadcrumbItem[] {
    if (!this.projectId) {
      return [];
    }

    return [
      {
        label: this.translateService.instant('project_breadcrumb_all_projects'),
        action: () => this.navigateToProjects()
      },
      {
        label: this.projectForm?.value?.displayName || this.currentProject?.displayName || ''
      }
    ];
  }

  async goToNextStep(): Promise<void> {
    if (this.currentStep === this.PROJECT_STEPS.SOLUTION && !(await this.canEnterResultStep())) {
      return;
    }
    this.currentStep++;
  }

  goToPreviousStep() {
    this.currentStep--;
  }

  hasAtLeastOneSolution(): boolean {
    return Boolean(this.currentProject?.solutions?.length && this.currentProject.solutions?.length > 0);
  }

  canAccessResultStep(): boolean {
    return this.hasAtLeastOneSolution() && !this.projectHasError;
  }

  private async canEnterResultStep(): Promise<boolean> {
    if (!this.canAccessResultStep()) return false;
    if (!this.hasUnsavedChanges) return true;

    return this.clickSave();
  }

  getResultStepTooltip(): string {
    if (!this.hasAtLeastOneSolution()) return this.translateService.instant('project_tooltip_result_step');
    if (this.projectHasError) return this.translateService.instant('project_tooltip_result_step_errors');
    return '';
  }

  async goToStep(step: number): Promise<void> {
    if (step === this.PROJECT_STEPS.RESULT && !(await this.canEnterResultStep())) {
      return;
    }
    this.currentStep = step;
  }

  isStepAccessible(stepIndex: number): boolean {
    if (!this.projectId && stepIndex > this.currentStep) {
      return false;
    }
    if (stepIndex === this.PROJECT_STEPS.RESULT && !this.canAccessResultStep()) {
      return false;
    }
    return true;
  }

  onStepClick(stepIndex: number): void {
    this.goToStep(stepIndex);
  }

  getStepTooltip(stepIndex: number): string {
    if (stepIndex === this.PROJECT_STEPS.RESULT && !this.canAccessResultStep()) {
      return this.getResultStepTooltip();
    }
    return '';
  }

  onDelete() {
    this.projectForm.reset();
  }

  onSubmit() {
    if (!this.projectForm.valid) return;

    if (this.currentStep < this.PROJECT_STEPS.RESULT) {
      this.goToNextStep();
    }
  }

  checkNatureProject() {
    return this.projectForm?.value?.projectType?.length > 0;
  }

  checkOperationType() {
    if (!this.projectForm.value?.solutions) return true;

    return this.projectForm.value.solutions.every((solution: Solution) => solution.operations?.every((operation: Operation) => operation.type !== undefined));
  }

  checkBiodiversity() {
    if (!this.projectForm.value?.useWaterAndBiodeversityIndicators) return true;
    if (!this.projectForm.value?.solutions) return true;

    return this.projectForm.value.solutions.every((solution: Solution) => {
      const waterValid = !(solution.waterIndicator !== undefined && solution.waterIndicator >= 2 && !solution.waterFileName);
      const bioValid = !(solution.biodiversityIndicator !== undefined && solution.biodiversityIndicator >= 2 && !solution.bioFileName);
      return waterValid && bioValid;
    });
  }

  checkVersionOnSave() {
    if (!this.projectId) return true;

    const isPending = this.projectForm.value.state === ProjectState.Pending;
    const isVersionChanged = this.projectForm.value.versionId !== this.projectForm.value.versionId;

    return isVersionChanged ? isPending : true;
  }

  majStore(snackbarMsgType: string | null) {
    this.projectService
      .getAll(history.state?.groupBy)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (arr) => {
          if (arr) {
            this.store.dispatch(new UpdateResourceAction(arr));
            if (snackbarMsgType) {
              this.snackbarService.open(snackbarMsgType);
            }
          }
        },
        (error) => {
          this.snackbarService.open(SnackbarTypes.error);
        }
      );
  }

  onUpdateOrSaveAch(currentItem: Project) {
    return this.projectId ? Promise.resolve(this.projectService.update(currentItem)) : Promise.resolve(this.projectService.add(currentItem));
  }

  private validateForm(silent = false): boolean {
    if (!this.checkNatureProject()) {
      if (!silent) this.snackbarService.showMessage(this.translateService.instant('error_project_nature_not_selected'));
      return false;
    }

    if (!this.checkOperationType()) {
      if (!silent) this.snackbarService.showMessage(this.translateService.instant('project_error_operation_type_not_selected'));
      return false;
    }

    if (!this.checkBiodiversity()) {
      if (!silent) this.snackbarService.showMessage(this.translateService.instant('project_error_biodiversity_proof_required'));
      return false;
    }

    if (!this.checkVersionOnSave()) {
      if (!silent) this.snackbarService.showMessage(this.translateService.instant('project_error_version_cannot_change'));
      return false;
    }

    if (this.projectForm.invalid) {
      if (!silent) this.snackbarService.showMessage(this.translateService.instant('project_error_fill_all_fields'));
      return false;
    }

    return true;
  }

  private async createDefaultScenario(projectId: string): Promise<void> {
    try {
      const defaultScenario: Scenario = {
        code: 'SCENARIO_DEFAULT',
        displayName: this.translateService.instant('project_default_scenario_name'),
        projectId: projectId,
        comment_tables: '',
        comment_general: '',
        comment_summary: '',
        solutions: [],
        isHybrid: false,
        indicatorSelected: this.indicators
          .filter((indicator) => {
            if ((indicator.code === 'PE' || indicator.code === 'PB') && !this.projectForm.value?.useWaterAndBiodeversityIndicators) {
              return false;
            }
            return true;
          })
          .map((indicator) => ({ ...indicator, isChecked: true }))
      };

      await this.projectService.addScenario(defaultScenario);
    } catch (error) {
      this.snackbarService.open(SnackbarTypes.error);
    }
  }

  private async linkProjectToCftIfNeeded(projectId: string | undefined): Promise<void> {
    if (!this.cftId || !projectId || this.projectId != null) return;
    try {
      await this.callForTenderService.linkProject(this.cftId, { cftId: this.cftId, projectId, selectedSolutionId: null, rank: 0, createdFromCFT: true }).toPromise();
    } catch {
      this.snackbarService.showMessage(this.translateService.instant('project_cft_link_failed'));
    }
  }

  private normalizeContractorObject(value: any): { id: string | null; displayName: string } | null {
    if (!value) return null;
    if (typeof value === 'object') return value;
    if (typeof value === 'string' && value.trim() !== '') {
      return { id: null, displayName: value.trim() };
    }
    return null;
  }

  private formatDateForServer(date: Date | string | null): string | null {
    if (!date) return null;
    if (typeof date === 'string') return date;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private async saveProject(): Promise<boolean> {
    this.spinnerService.show();
    let saved = false;

    try {
      const formValue = this.projectForm.value;
      const itemToSave = Object.assign(this.currentProject, formValue);
      itemToSave.projectContractorObject = this.normalizeContractorObject(formValue.projectContractorObject);
      itemToSave.offerSubmissionDate = this.formatDateForServer(formValue.offerSubmissionDate);
      itemToSave.reportTransmissionDate = this.formatDateForServer(formValue.reportTransmissionDate);
      itemToSave.callForTendersDisplayName = itemToSave.callForTendersDisplayName;
      if (!itemToSave.displayName && itemToSave.title1) {
        itemToSave.displayName = itemToSave.title1;
      }

      const rep = await this.onUpdateOrSaveAch(itemToSave);
      const data: Project = await rep.toPromise();
      saved = true;
      this.autoSaveService.markSaved();
      this.projectForm.markAsPristine();
      this.majStore(SnackbarTypes.add);
      if (this.projectId == null && data.id) {
        const { versionId, ...rest } = data;
        this.projectForm.patchValue(rest);
        await this.createDefaultScenario(data.id);
        await this.linkProjectToCftIfNeeded(data.id);
        this.projectService.projectCreated$.next(data);
      } else {
        this.updateProductCeuValues(data);
        this.uploadPendingEquipmentProofs();
      }
      if (history.state) history.state.projectId = data.id;
      this.router.navigate([`${SiteMapUrl.MenuProject}`, data.id], {
        state: {
          ...history.state,
          step: this.currentStep + 1
        }
      });
    } catch (error) {
      if (!saved) {
        this.snackbarService.open(SnackbarTypes.error);
        return saved;
      }
      console.error(error);
    } finally {
      this.spinnerService.hide();
    }

    return saved;
  }

  async clickSave(): Promise<boolean> {
    if (!this.validateForm()) return false;
    return this.saveProject();
  }

  async handleSaveFromSolutions(currentItem: Project, silent = false) {
    if (!this.validateForm(silent)) return;

    this.spinnerService.show();

    try {
      const formValue = { ...this.projectForm.value };
      const { projectContractorObject, ...rest } = formValue;
      const normalizedContractor = this.normalizeContractorObject(projectContractorObject) || currentItem.projectContractorObject;
      const itemToSave = Object.assign({}, currentItem, rest, {
        projectContractorObject: normalizedContractor,
        offerSubmissionDate: this.formatDateForServer(formValue.offerSubmissionDate),
        reportTransmissionDate: this.formatDateForServer(formValue.reportTransmissionDate)
      });
      if (!itemToSave.displayName && itemToSave.title1) {
        itemToSave.displayName = itemToSave.title1;
      }
      const rep = await this.onUpdateOrSaveAch(itemToSave);

      rep.subscribe({
        next: async (data: Project) => {
          this.majStore(silent ? null : SnackbarTypes.add);
          this.autoSaveService.markSaved();
          this.projectForm.markAsPristine();
          if (this.projectId == null) {
            this.currentProject = data;
            this.projectForm.patchValue(data);
            await this.linkProjectToCftIfNeeded(data.id);
            this.projectService.projectCreated$.next(data);
          } else {
            this.updateProductCeuValues(data);
            this.uploadPendingEquipmentProofs();
          }
          if (history.state) history.state.projectId = data.id;
          this.spinnerService.hide();
        },
        error: () => {
          this.snackbarService.open(SnackbarTypes.error);
          this.spinnerService.hide();
        }
      });
    } catch (error) {
      this.snackbarService.open(SnackbarTypes.error);
      this.spinnerService.hide();
    }
  }

  private updateProductCeuValues(data: Project): void {
    for (const sol of data.solutions ?? []) {
      const currentSol = this.currentProject.solutions?.find((s) => s.id === sol.id);
      if (!currentSol) continue;
      for (const op of sol.operations ?? []) {
        const currentOp = currentSol.operations?.find((o) => o.id === op.id);
        if (!currentOp?.detailedOperation) continue;
        for (const type of ['inputProducts', 'outputProducts', 'vehicles'] as const) {
          const sourceItems = op.detailedOperation?.[type] as any[];
          const targetItems = currentOp.detailedOperation?.[type] as any[];
          if (!sourceItems || !targetItems) continue;
          const idKey = type === 'vehicles' ? 'materialCompositionId' : 'operationCompositionId';
          targetItems.forEach((target) => {
            const source = sourceItems.find((s) => s[idKey] && s[idKey] === target[idKey]);
            if (!source) return;
            target.ceuValues = source.ceuValues;
            target.resourceNotAvailableForThisVersion = source.resourceNotAvailableForThisVersion;
            if (type !== 'vehicles') target.transportationNotAvailableForThisVersion = source.transportationNotAvailableForThisVersion;
          });
        }
        assignCreatedEquipmentIds(currentOp, op);
      }
    }
    this.refreshProjectErrors();
  }

  addPendingEquipmentProof(proof: PendingEquipmentProof): void {
    this.pendingEquipmentProofs = [...this.pendingEquipmentProofs.filter((p) => p.item !== proof.item), proof];
  }

  private uploadPendingEquipmentProofs(): void {
    const proofs = this.pendingEquipmentProofs.filter((p) => p.item.fileName);
    this.pendingEquipmentProofs = [];
    proofs.forEach((proof) => {
      if (!proof.item.id) {
        this.snackbarService.open(SnackbarTypes.error);
        return;
      }
      const formData = new FormData();
      formData.append('file', proof.file);
      const upload$ = proof.kind === 'dredging' ? this.uploadService.uploadDredgeProof(proof.item.id, formData) : this.uploadService.uploadTunnelingProof(proof.item.id, formData);
      upload$.subscribe({
        error: () => this.snackbarService.open(SnackbarTypes.error)
      });
    });
  }

  onShare() {
    const shareDialogRef = this.dialog.open(DialogShareProjectComponent, {
      data: { projectIds: [this.projectId] }
    });

    shareDialogRef.afterClosed().subscribe((dataResult) => {});
  }

  onDuplicateProject() {
    this.projectService.duplicate(this.projectId!).subscribe(
      (res) => {
        if (res) {
          this.majStore(SnackbarTypes.duplicate);
          this.projectService.projectCreated$.next(res);
        }
      },
      (error) => {
        this.snackbarService.openServerError(error);
      }
    );
  }

  onDeleteDialog() {
    let title = this.translateService.instant('project_confirm_delete');

    const dialogRef = this.dialog.open(DialogOverviewComponent, {
      data: { title: title }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.projectService.delete(this.projectId!).subscribe(
          () => {
            this.projectService.projectDeleted$.next([this.projectId!]);
            this.autoSaveService.clear(this.projectId);
            this.autoSaveService.markSaved();
            this.snackbarService.open(SnackbarTypes.delete);
            this.router.navigate([SiteMapUrl.MenuProject]);
          },
          (error) => {
            this.snackbarService.open(SnackbarTypes.error);
          }
        );
      }
    });
  }

  changeProjectAgencyOwner() {
    if (!this.projectId) return;

    this.spinnerService.show();
    this.agencyService
      .getAll()
      .subscribe((data) => {
        const dialogRef = this.dialog.open(DialogSelectAgencyComponent, {
          data: data
        });

        dialogRef.afterClosed().subscribe((dataResult) => {
          if (dataResult && dataResult.agencyId) {
            this.projectService.changeProjectAgencyOwner(this.projectId!, dataResult.agencyId).subscribe(
              (response) => {
                this.projectForm.patchValue({
                  owner: response.owner,
                  ownerId: response.ownerId,
                  scopeId: response.scopeId
                });
                this.snackbarService.open(SnackbarTypes.projectAgencyOwner);
                this.spinnerService.hide();
              },
              (error) => {
                this.snackbarService.open(SnackbarTypes.error);
                this.spinnerService.hide();
              }
            );
          }
        });
      })
      .add(() => {
        this.spinnerService.hide();
      });
  }

  onPublish() {
    if (!this.projectId) return;

    this.projectService.getEUsToPublishProject(this.projectId).subscribe((data) => {
      const dialogRef = this.dialog.open(DialogSelectEuPublishComponent, {
        data: data
      });

      dialogRef.afterClosed().subscribe((dataResult) => {
        if (dataResult && dataResult.euToPublishId && dataResult.publicationDate) {
          const newPublishProject: PublishProject = {
            projectId: this.projectId!,
            euId: dataResult.euToPublishId,
            publicationDate: dataResult.publicationDate
          };

          this.projectService.publishProject(newPublishProject).subscribe(
            (response) => {
              this.projectForm.patchValue({
                visualisationDate: response.visualisationDate,
                isPublished: response.isPublished,
                euPublishedDisplayName: response.euPublishedDisplayName
              });
              this.snackbarService.open(SnackbarTypes.publish);
            },
            (error) => {
              this.snackbarService.open(SnackbarTypes.error);
            }
          );
        }
      });
    });
  }

  private slugify(text: string): string {
    return text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^\w\-]+/g, '')
      .replace(/\-\-+/g, '-')
      .replace(/^-+/, '')
      .replace(/-+$/, '');
  }

  private downloadWithLegacyMethod(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = this.slugify(fileName) + '.sevetp';
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  }

  onExport() {
    const dialogRef = this.dialog.open(ConfirmCancelDialogComponent, {
      data: {
        message: this.translateService.instant('project_confirm_export'),
        confirmText: this.translateService.instant('project_confirm_export_btn'),
        cancelText: this.translateService.instant('project_confirm_cancel_btn'),
        confirmColor: 'accent'
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.projectService.exportProject(this.projectId!).subscribe(
          (blob: Blob) => {
            this.downloadWithLegacyMethod(blob, this.projectForm.value.title1 || 'project');
          },
          (error) => {
            if (error?.status === 403 || String(error).includes('Error Status: 403')) {
              this.snackbarService.showMessage(this.translateService.instant('project_export_resource_unavailable'));
            } else {
              this.snackbarService.openServerError(error);
            }
          }
        );
      }
    });
  }

  navigateToProjects() {
    this.router.navigate([SiteMapUrl.MenuProject]);
  }
}
